import getStudies from './studiesList';
import { DicomMetadataStore, log, utils, Enums } from '@ohif/core';
import isSeriesFilterUsed from '../../utils/isSeriesFilterUsed';
import ActivityService from '../../services/ActivityService';
import SecureStorageService from '../../services/SecureStorageService';

const { getSplitParam } = utils;

/**
 * Initialize the route.
 *
 * @param props.servicesManager to read services from
 * @param props.studyInstanceUIDs for a list of studies to read
 * @param props.dataSource to read the data from
 * @param props.filters filters from query params to read the data from
 * @returns array of subscriptions to cancel
 */
export async function defaultRouteInit(
  { servicesManager, studyInstanceUIDs, dataSource, filters, appConfig }: withAppTypes,
  hangingProtocolId,
  stageIndex
) {
  // Clear annotation data immediately so toolbar (e.g. Grading Form) shows disabled until current study's data loads
  if (typeof window !== 'undefined') {
    window.annotationsData = undefined;
  }

  try {
    const { resetAnnotationLifecycleState } = await import(
      '../../../../../dicom-grader-app/src/components/FloatingFormInline'
    );
    await resetAnnotationLifecycleState?.();
  } catch (err) {
    console.warn('[Viewer Init] Failed to reset annotation lifecycle:', err);
  }

  // Check if required sessionStorage items are missing (e.g., after logout/login)
  // If missing, redirect to submissions page
  // Try to get from encrypted storage first, then fallback to unencrypted
  const secureStorage = SecureStorageService.getInstance();
  let currentStudyUid: string | null = null;
  let currentJobId: string | null = null;

  const initialUrlParams = new URLSearchParams(window.location.search);
  const initialUidFromUrl = getSplitParam('studyinstanceuids', initialUrlParams)?.[0] || null;
  currentStudyUid = initialUidFromUrl || sessionStorage.getItem('currentStudyUid');
  currentJobId = sessionStorage.getItem('currentJobId');

  if (!currentStudyUid || !currentJobId) {
    try {
      currentStudyUid = currentStudyUid || (await secureStorage.getSessionItem('currentStudyUid'));
      currentJobId = currentJobId || (await secureStorage.getSessionItem('currentJobId'));
    } catch (error) {
      // Fallback to unencrypted for migration
      console.warn('[Viewer Init] Failed to read encrypted storage, trying unencrypted:', error);
      currentStudyUid = currentStudyUid || sessionStorage.getItem('currentStudyUid');
      currentJobId = currentJobId || sessionStorage.getItem('currentJobId');
    }
  }

  if (!currentStudyUid || !currentJobId) {
    console.warn('[Viewer Init] Missing required sessionStorage items (currentStudyUid or currentJobId). Redirecting to /submissions');
    window.location.href = '/submissions';
    return []; // Return empty subscriptions array
  }

  const { displaySetService, hangingProtocolService, uiNotificationService, customizationService } =
    servicesManager.services;
  /**
   * Function to apply the hanging protocol when the minimum number of display sets were
   * received or all display sets retrieval were completed
   * @returns
   */
  function applyHangingProtocol() {
    const displaySets = displaySetService.getActiveDisplaySets();

    if (!displaySets || !displaySets.length) {
      return;
    }

    // Gets the studies list to use
    const studies = getStudies(studyInstanceUIDs, displaySets);

    // study being displayed, and is thus the "active" study.
    const activeStudy = studies[0];

    // run the hanging protocol matching on the displaySets with the predefined
    // hanging protocol in the mode configuration
    hangingProtocolService.run({ studies, activeStudy, displaySets }, hangingProtocolId, {
      stageIndex,
    });
  }

  const unsubscriptions = [];
  const issuedWarningSeries = [];
  let annotationsApiCalled = false; // Track if we've already called the annotations API
  let annotationsApiRequestSeq = 0;

  const getStudyUidFromUrl = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = getSplitParam('studyinstanceuids', params);
    return fromUrl?.[0] || null;
  };

  const getCurrentViewerStudyUid = async (): Promise<string | null> => {
    const fromUrl = getStudyUidFromUrl();
    if (fromUrl) {
      return fromUrl;
    }

    const fromSession = sessionStorage.getItem('currentStudyUid');
    if (fromSession) {
      return fromSession;
    }

    try {
      return await secureStorage.getSessionItem('currentStudyUid');
    } catch {
      return null;
    }
  };

  // Function to call annotations API
  const callAnnotationsApi = async () => {
    if (annotationsApiCalled) return;
    annotationsApiCalled = true;
    const requestSeq = ++annotationsApiRequestSeq;

    // Reset viewer timer state for every study load to avoid carrying over
    // elapsed time from the previous study. The Activity API will seed it if
    // history exists; otherwise it stays at zero.
    (window as any).viewerTimerState = {
      elapsedSeconds: 0,
      lastLoggedSeconds: 0,
    };
    (window as any).viewerInitComplete = false;

    // Clear any existing annotation data from previous studies
    window.annotationsData = undefined;
    // console.log('[Viewer Init] Cleared previous annotation data');

    // Clear any floating form state that might be cached
    if (window.floatingFormStore) {
      console.log('[Viewer Init] Clearing floating form store state');
      // Reset any cached form state
    }

    // Use URL/session first so direct study-list navigation cannot race async
    // encrypted storage writes and restore annotations for the previous study.
    const studyUid = await getCurrentViewerStudyUid();

    if (!studyUid) {
      console.warn('[Viewer Init] No study_uid found in sessionStorage');
      return;
    }

    // Get job_id from session first to avoid reading stale encrypted data
    // during direct study-list navigation.
    let jobId: string | null = sessionStorage.getItem('currentJobId');
    if (!jobId) {
      try {
        jobId = await secureStorage.getSessionItem('currentJobId');
      } catch {
        jobId = null;
      }
    }
    if (jobId) {
      // console.log('[Viewer Init] Found job_id in sessionStorage:', jobId);
    } else {
      console.warn('[Viewer Init] No job_id found in sessionStorage');
    }

    // console.log('[Viewer Init] Calling annotations API for study:', studyUid);

    // Import and use ApiService (same pattern as StudiesList)
    import('../../services/ApiService').then(async ({ default: ApiService }) => {
      try {
        const apiService = ApiService.getInstance();
        const baseUrl = apiService.getGradingServiceUrl();
        const endpoint = `/annotations/study/${studyUid}`;

        // Build query parameters - include job_id if available
        const queryParams: Record<string, any> = {};
        if (jobId) {
          queryParams.job_id = jobId;
        }

        // console.log('[Viewer Init] Fetching from:', `${baseUrl}${endpoint}`, queryParams);

        // Use ApiService.get() which includes authorization headers automatically
        // Pass queryParams as second argument to add job_id as query parameter
        const response = await apiService.get(endpoint, Object.keys(queryParams).length > 0 ? queryParams : undefined, baseUrl);

        const latestStudyUid = await getCurrentViewerStudyUid();
        if (requestSeq !== annotationsApiRequestSeq || latestStudyUid !== studyUid) {
          console.log('[Viewer Init] Ignoring stale annotations response:', {
            requestedStudyUid: studyUid,
            latestStudyUid,
          });
          return;
        }

        if (response.success && response.data) {
          // console.log('[Viewer Init] Annotations data loaded:', response.data);
          window.annotationsData = response.data;

          // Let any interested components know that annotations data (including
          // study.meta.validator.warnings) has been updated.
          try {
            window.dispatchEvent(new CustomEvent('annotations-data-updated'));
          } catch {
            // Silently ignore event dispatch errors
          }

          // Now that we have annotations data (with study_id and annotation_id),
          // call activity API to pre-populate timer from previous visits
          // console.log('[Viewer Init] Calling activity API to seed timer from history...');
          try {
            const activityService = ActivityService.getInstance();
            await activityService.seedTimerFromHistory();
            // console.log('[Viewer Init] Activity API call completed, timer seeded');
          } catch (seedError) {
            console.error('[Viewer Init] Failed to seed timer from history:', seedError);
          }

          // Mark viewer init as complete so timer can start
          (window as any).viewerInitComplete = true;

          // Restore annotations to viewport
          import('../../../../../dicom-grader-app/src/components/FloatingFormInline')
            .then(({ restoreCornerstoneAnnotationsFromData }) => {
              if (requestSeq !== annotationsApiRequestSeq) {
                return;
              }
              restoreCornerstoneAnnotationsFromData(response.data);
            })
            .catch(err => console.error('[Viewer Init] Error:', err));
        } else {
          console.error('[Viewer Init] API call failed:', response.error);
          (window as any).viewerInitComplete = true;
        }

        // Keep sessionStorage for reloads - don't clear it
        // console.log('[Viewer Init] Keeping instance_uid in sessionStorage for reloads');
      } catch (error) {
        console.error('[Viewer Init] Error fetching annotations:', error);
        (window as any).viewerInitComplete = true;
      }
    }).catch(error => {
      console.error('[Viewer Init] Error importing ApiService:', error);
      (window as any).viewerInitComplete = true;
    });
  };

  // Call API immediately if we have study_uid (for reloads)
  (async () => {
    const studyUid = await getCurrentViewerStudyUid();
    if (studyUid) {
      callAnnotationsApi();
    }
  })();

  // Store studyUid for comparison (will be set asynchronously)
  let storedStudyUid: string | null = currentStudyUid;
  (async () => {
    storedStudyUid = await getCurrentViewerStudyUid();
  })();

  // Reset API call flag when study changes
  const checkForStudyChange = async () => {
    const currentStudyUidValue = await getCurrentViewerStudyUid();
    if (currentStudyUidValue && currentStudyUidValue !== storedStudyUid) {
      console.log('[Viewer Init] Study changed, resetting API call flag');
      storedStudyUid = currentStudyUidValue;
      annotationsApiCalled = false;
      window.annotationsData = undefined;
      callAnnotationsApi();
    }
  };

  // Check for study changes periodically
  const studyChangeInterval = setInterval(checkForStudyChange, 1000);

  const { unsubscribe: instanceAddedUnsubscribe } = DicomMetadataStore.subscribe(
    DicomMetadataStore.EVENTS.INSTANCES_ADDED,
    function ({ StudyInstanceUID, SeriesInstanceUID, madeInClient = false }) {
      const seriesMetadata = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);

      // checks if the series filter was used, if it exists
      const seriesInstanceUIDs = filters?.seriesInstanceUID;
      if (
        seriesInstanceUIDs?.length &&
        !isSeriesFilterUsed(seriesMetadata.instances, filters) &&
        !issuedWarningSeries.includes(seriesInstanceUIDs[0])
      ) {
        // stores the series instance filter so it shows only once the warning
        issuedWarningSeries.push(seriesInstanceUIDs[0]);
        uiNotificationService.show({
          title: 'Series filter',
          message: `Each of the series in filter: ${seriesInstanceUIDs} are not part of the current study. The entire study is being displayed`,
          type: 'error',
          duration: 7000,
        });
      }

      // T0518 TODO: Temporarily hide SM modality (e.g., show OP only)
      const filteredInstances = (seriesMetadata.instances || []).filter(
        instance => instance?.Modality !== 'SM'
      );
      if (filteredInstances.length > 0) {
        displaySetService.makeDisplaySets(filteredInstances, { madeInClient });
      }

      // Call annotations API when instances are loaded (if not already called)
      if (!annotationsApiCalled) {
        callAnnotationsApi();
      }
    }
  );

  unsubscriptions.push(instanceAddedUnsubscribe);

  log.time(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
  log.time(Enums.TimingEnum.STUDY_TO_FIRST_IMAGE);

  const allRetrieves = studyInstanceUIDs.map(StudyInstanceUID =>
    dataSource.retrieve.series.metadata({
      StudyInstanceUID,
      filters,
      returnPromises: true,
      sortCriteria: customizationService.getCustomization('sortingCriteria'),
    })
  );

  // log the error if this fails, otherwise it's so difficult to tell what went wrong...
  allRetrieves.forEach(retrieve => {
    retrieve.catch(error => {
      console.error(error);
    });
  });

  // is displaysets from URL and has initialSOPInstanceUID or initialSeriesInstanceUID
  // then we need to wait for all display sets to be retrieved before applying the hanging protocol
  const params = new URLSearchParams(window.location.search);

  const initialSeriesInstanceUID = getSplitParam('initialseriesinstanceuid', params);
  const initialSOPInstanceUID = getSplitParam('initialsopinstanceuid', params);

  let displaySetFromUrl = false;
  if (initialSeriesInstanceUID || initialSOPInstanceUID) {
    displaySetFromUrl = true;
  }

  await Promise.allSettled(allRetrieves).then(async promises => {
    log.timeEnd(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_FIRST_IMAGE);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_ALL_IMAGES);

    const allPromises = [];
    const remainingPromises = [];

    function startRemainingPromises(remainingPromises) {
      remainingPromises.forEach(p => p.forEach(p => p.start()));
    }

    promises.forEach(promise => {
      const retrieveSeriesMetadataPromise = promise.value;
      if (!Array.isArray(retrieveSeriesMetadataPromise)) {
        return;
      }

      if (displaySetFromUrl) {
        const requiredSeriesPromises = retrieveSeriesMetadataPromise.map(promise =>
          promise.start()
        );
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
      } else {
        const { requiredSeries, remaining } = hangingProtocolService.filterSeriesRequiredForRun(
          hangingProtocolId,
          retrieveSeriesMetadataPromise
        );
        const requiredSeriesPromises = requiredSeries.map(promise => promise.start());
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
        remainingPromises.push(remaining);
      }
    });

    await Promise.allSettled(allPromises).then(applyHangingProtocol);
    startRemainingPromises(remainingPromises);
    applyHangingProtocol();
  });

  // Add interval cleanup to unsubscriptions
  unsubscriptions.push(() => {
    clearInterval(studyChangeInterval);
  });

  return unsubscriptions;
}
